import { useState, useEffect, useRef, useCallback } from 'react';
import { useFlags } from 'flagsmith/react';
import { useEquipment, useServiceOptions, useWorkOrderSubmit, useTeamMemberByUserId } from '../../../hooks';
import DescribeProblem from '../../organisms/DescribeProblem';
import EquipmentInfoSection from '../../organisms/EquipmentInfoSection';
import EquipActionForm from '../../organisms/EquipActionForm';
import styles from './EquipActionPortal.module.css';
import { useUser } from '@pella/react-layout';
import { useTranslation } from 'react-i18next';
import { Modal, ModalRoot, ModalHeader, ModalContent, ModalFooter, Button } from '@pella/sash-framework';
import { useCreateUserAnalytics } from '../../../hooks/useMaintenanceCopilot/useMaintenanceCopilot';

const createAnalyticsRowId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  // Fallback to a UUID v4-shaped value in environments without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replaceAll(/[xy]/gu, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === 'x' ? random : (random % 4) + 8;
    return value.toString(16);
  });
};

const formatTimeOnly = (timestamp) => {
  if (!timestamp) return null;

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

/**
 * Equipment Action Portal page component
 * Allows users to report equipment actions such as line downtime, machine down, or scheduled work
 * @returns {import('react').ReactElement} The EquipActionPortal component
 */
const EquipActionPortal = () => {
  const [comment, setComment] = useState('');
  const [userNumber, setUserNumber] = useState('');
  const clearFormRef = useRef(null);
  const analyticsWorkOrderIdRef = useRef('');
  const { t } = useTranslation();
  const flags = useFlags(['maintenance-copilot']);
  const isMaintenanceCopilotFlagEnabled = flags['maintenance-copilot']?.enabled;
  const equipment = useEquipment();
  const user = useUser();
  const { userid, email: rawUserEmail } = user ?? {};
  const { data: employeeDetails } = useTeamMemberByUserId(userid);
  const serviceOptions = useServiceOptions();
  const { mutate: upsertUserAnalytics } = useCreateUserAnalytics();
  const userEmail = rawUserEmail || '';
  const selectedEquipment = equipment.selectedEquipment;
  const analyticsIdentity = /** @type {{email?: string, usr_login?: string, usr_emp_no?: string}} */ (
    employeeDetails ?? {}
  );
  const { scanAgainVisible, handleScanAgain } = equipment;
  const {
    serviceAreaSelected,
    serviceActionSelected,
    filteredServiceAreas,
    filteredServiceActions,
    handleServiceAreaChange,
    handleServiceActionChange,
  } = serviceOptions;
  const workOrder = useWorkOrderSubmit({
    selectedEquipment,
    serviceAreaSelected,
    serviceActionSelected,
    employeeDetails,
    comment,
    isMaintenanceCopilotFlagEnabled,
    onSuccess: () => clearFormRef.current?.(),
    onConversationCreated: (desc) => setComment(desc),
  });
  const {
    conversationId,
    handleSummaryReady,
    followUpQuestions,
    setFollowUpQuestions,
    handleSummaryError,
    downtimeStartTime,
    isSubmitting,
    loadingCreateConversation,
    handleSubmit,
    handleCreateConversation: createConversation,
    retryDialogOpen,
    retryDialogMessage,
    handleRetryDialogClose,
  } = workOrder;

  // Sync employee number at render time when data loads (replaces effect)
  const [prevEmpNo, setPrevEmpNo] = useState(undefined);
  if (analyticsIdentity.usr_emp_no && analyticsIdentity.usr_emp_no !== prevEmpNo) {
    setPrevEmpNo(analyticsIdentity.usr_emp_no);
    setUserNumber(analyticsIdentity.usr_emp_no);
  }

  const clearForm = useCallback(() => {
    setComment('');
    setUserNumber(analyticsIdentity.usr_emp_no ?? '');
    analyticsWorkOrderIdRef.current = '';
    equipment.reset();
    serviceOptions.reset();
    workOrder.reset();
  }, [analyticsIdentity.usr_emp_no, equipment, serviceOptions, workOrder]);

  useEffect(() => {
    clearFormRef.current = clearForm;
  }, [clearForm]);

  const handleCreateConversation = () => {
    const lastEntryAt = Date.now();
    const analyticsWorkOrderId = createAnalyticsRowId();
    const derivedEmail =
      analyticsIdentity.email ||
      (analyticsIdentity.usr_login ? `${analyticsIdentity.usr_login}@pella.com` : '') ||
      userEmail ||
      '';

    analyticsWorkOrderIdRef.current = analyticsWorkOrderId;

    upsertUserAnalytics({
      id: analyticsWorkOrderId,
      lastEntryAt,
      userId: userid || '',
      userEmail: String(derivedEmail).toLowerCase(),
      empNumber: userNumber,
      equipmentNumber: selectedEquipment?.equipNum || '',
      siteId: selectedEquipment?.siteId || '',
      serviceArea: serviceAreaSelected?.serviceArea || '',
      serviceAction: serviceActionSelected?.itemValue || serviceActionSelected?.item || '',
      comment,
    });

    createConversation();
  };

  /**
   * Persist analytics collected from the DescribeProblem flow.
   * @param {{ outcome: string, followUpQuestions: Array, arrivedAt: number, cancellationReason?: string, cancellationReasonDetails?: string }} data
   */
  const handleDescribeProblemAnalytics = (data) => {
    const { outcome, followUpQuestions: questions, arrivedAt, cancellationReason, cancellationReasonDetails } = data;

    const lastEntryAt = Date.now();
    const analyticsWorkOrderId = analyticsWorkOrderIdRef.current || createAnalyticsRowId();
    const derivedEmail =
      analyticsIdentity.email ||
      (analyticsIdentity.usr_login ? `${analyticsIdentity.usr_login}@pella.com` : '') ||
      userEmail ||
      '';

    upsertUserAnalytics({
      id: analyticsWorkOrderId,
      lastEntryAt,
      userId: userid || '',
      userEmail: String(derivedEmail).toLowerCase(),
      empNumber: userNumber,
      equipmentNumber: selectedEquipment?.equipNum || '',
      siteId: selectedEquipment?.siteId || '',
      serviceArea: serviceAreaSelected?.serviceArea || '',
      serviceAction: serviceActionSelected?.itemValue || serviceActionSelected?.item || '',
      comment,
      outcome,
      cancellationReason: cancellationReason || '',
      cancellationReasonDetails: cancellationReasonDetails || '',
      arrivedAt: formatTimeOnly(arrivedAt),
      questionAnswers: (questions || []).map((q) => ({
        question: q.text,
        answer: q.selectedAnswer,
        additionalDetails: q.additionalDetails || '',
        answeredAt: formatTimeOnly(q.answeredAt),
      })),
    });
  };

  if (conversationId) {
    return (
      <DescribeProblem
        conversationId={conversationId}
        equipmentNumber={selectedEquipment.equipNum}
        siteId={selectedEquipment.siteId}
        setSummaryText={handleSummaryReady}
        onCloseRequestCompleted={clearForm}
        followUpQuestions={followUpQuestions}
        setFollowUpQuestions={setFollowUpQuestions}
        onCloseSummaryGenerationErrorModal={handleSummaryError}
        handleSubmitWorkOrder={handleSubmit}
        downtimeStartTime={downtimeStartTime}
        onRecordAnalytics={handleDescribeProblemAnalytics}
      />
    );
  }

  return (
    <div className={styles.container}>
      <EquipmentInfoSection
        selectedEquipment={selectedEquipment}
        scanAgainVisible={scanAgainVisible}
        handleScanAgain={handleScanAgain}
      />
      <EquipActionForm
        comment={comment}
        onCommentChange={(e) => setComment(e.target.value)}
        empNumber={userNumber}
        onEmpNumberChange={(e) => setUserNumber(e.target.value)}
        serviceAreaSelected={serviceAreaSelected}
        serviceActionSelected={serviceActionSelected}
        filteredServiceAreas={filteredServiceAreas}
        filteredServiceActions={filteredServiceActions}
        onServiceAreaChange={handleServiceAreaChange}
        onServiceActionChange={handleServiceActionChange}
        selectedEquipment={selectedEquipment}
        isSubmitting={isSubmitting}
        loadingCreateConversation={loadingCreateConversation}
        isMaintenanceCopilotFlagEnabled={isMaintenanceCopilotFlagEnabled}
        onSubmit={handleSubmit}
        onCancel={clearForm}
        onCreateConversation={handleCreateConversation}
      />
      <ModalRoot
        open={retryDialogOpen}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleRetryDialogClose(false);
        }}
      >
        <Modal showClose={false}>
          <ModalHeader>{t('error')}</ModalHeader>
          <ModalContent>
            <p>{retryDialogMessage}</p>
          </ModalContent>
          <ModalFooter>
            <Button size='sm' color='secondary' onClick={() => handleRetryDialogClose(false)}>
              {t('cancel')}
            </Button>
            <Button size='sm' color='primary' onClick={() => handleRetryDialogClose(true)}>
              {t('submitWorkOrder')}
            </Button>
          </ModalFooter>
        </Modal>
      </ModalRoot>
    </div>
  );
};

export default EquipActionPortal;
